import { useState } from "react";
import { CreditCard, Gift, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { PlanToggle } from "@/components/PlanToggle";
import { PromoCodeBox } from "@/components/PromoCodeBox";
import { PerkList, PriceBlock, PRO_PERKS, fmtDate } from "@/components/PriceBlock";
import { api, formatApiError } from "@/lib/api";

const goTo = async (path, body, key, setBusy) => {
  setBusy(true);
  try {
    const { data } = await api.post(path, { origin_url: window.location.origin, ...body });
    window.location.href = data[key];
  } catch (e) {
    toast.error(formatApiError(e));
    setBusy(false);
  }
};

export const UpgradePanel = ({ s, initialInterval }) => {
  const [interval, setPeriod] = useState(initialInterval);
  const [promo, setPromo] = useState(null);
  const [busy, setBusy] = useState(false);
  const promo_code = promo?.interval === interval ? promo.code : undefined;
  return (
    <>
      <PlanToggle value={interval} onChange={setPeriod} />
      <PriceBlock interval={interval} prices={s.prices} promo={promo} />
      <PerkList perks={PRO_PERKS} />
      <PromoCodeBox interval={interval} promo={promo} onChange={setPromo} />
      <Button onClick={() => goTo("/payments/checkout", { interval, promo_code }, "checkout_url", setBusy)} disabled={busy}
        className="w-full rounded-full h-11 bg-amber-500 hover:bg-amber-400 text-onaccent font-semibold" data-testid="upgrade-pro-btn">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Upgrade to Pro
      </Button>
    </>
  );
};

export const SubscriptionPanel = ({ s, onToggleCancel, busy }) => {
  const [portalBusy, setPortalBusy] = useState(false);
  const end = s.current_period_end ? fmtDate(s.current_period_end) : "";
  return (
    <>
      <PriceBlock interval={s.interval} prices={s.prices} />
      <PerkList perks={PRO_PERKS} />
      <div className="space-y-3">
        <p className="text-sm text-slate-300" data-testid="pro-status-text">
          Pro · {s.interval === "year" ? "Yearly" : "Monthly"} · {s.cancel_at_period_end ? `ends ${end}` : `renews ${end}`}
        </p>
        {s.has_portal && (
          <Button onClick={() => goTo("/billing/portal", {}, "url", setPortalBusy)} disabled={portalBusy} className="w-full rounded-full bg-amber-500 hover:bg-amber-400 text-onaccent font-semibold" data-testid="manage-billing-btn">
            {portalBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Manage billing
          </Button>
        )}
        <p className="text-[11px] text-slate-500">Switch between monthly and yearly in Manage billing. Stripe works out any prorated charge or credit.</p>
        <Button onClick={onToggleCancel} disabled={busy} variant="outline" className="w-full rounded-full bg-transparent" data-testid={s.cancel_at_period_end ? "resume-subscription-btn" : "cancel-subscription-btn"}>
          {busy && <Loader2 className="w-4 h-4 animate-spin" />}{s.cancel_at_period_end ? "Resume subscription" : "Cancel subscription"}
        </Button>
      </div>
    </>
  );
};

export const GiftStatusPanel = ({ s }) => (
  <>
    <PerkList perks={PRO_PERKS} />
    <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 space-y-2">
      <p className="text-sm text-amber-100 flex items-center gap-2" data-testid="pro-status-text"><Gift className="w-4 h-4 text-amber-400" /> Pro · Gift · ends {fmtDate(s.gift_until)}</p>
      <p className="text-xs text-slate-400">Someone gave you a year of unlimited worlds. When it ends you can subscribe to keep Pro. Your worlds always stay yours.</p>
    </div>
  </>
);
